import { useState } from 'react';
import { Badge } from '@/ui/Badge';
import { Button } from '@/ui/Button';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Skeleton, SkeletonFilas } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { cn } from '@/lib/cn';
import { metaDeModalidad } from './piezas';
import {
  useCancelarInscripcion, useDesactivar, useEvento, useInscribirse, useInscritos, usePublicarEnMuro,
  yaPaso, type EventoConGaleria,
} from './api';

/**
 * DetalleDeEvento.tsx — las opciones 2 y 4: el detalle de un evento y sus inscritos.
 *
 * En panel lateral y no en modal: lo de detrás —la lista o el día del
 * calendario— sigue importando. Y así las confirmaciones pueden abrirse
 * **encima** sin cerrarlo.
 *
 * Eso es lo que no podía hacer el vanilla. Su pie tenía
 *
 *     onclick="PageEvents.cancelReg(id); Utils.closeModal()"
 *
 * y `cancelReg` abre la confirmación en el MISMO `#modal-overlay`, así que la
 * segunda mitad de la línea la cerraba en el acto. Medido el 2026-09-17: la
 * persona pulsaba «Cancelar inscripción», el diálogo desaparecía y no se
 * enviaba nada (0 `DELETE`).
 *
 * **Todo el texto va como texto.** El vanilla pasaba el título a
 * `Utils.modal({ title })`, que lo asigna con `innerHTML`, y la descripción
 * cruda al cuerpo: un `<img onerror>` en cualquiera de los dos se ejecutaba al
 * abrir el detalle. Los dos los escribe quien gestiona y los abre todo el portal.
 */
export function DetalleDeEvento({
  id, onCerrar, onEditar,
}: {
  id: number | null;
  onCerrar: () => void;
  onEditar: (e: EventoConGaleria) => void;
}) {
  const puedeGestionar = Auth.can('eventos', 'manage');
  const consulta = useEvento(id);
  const e = consulta.data;

  const inscribirse = useInscribirse();
  const cancelar = useCancelarInscripcion();
  const desactivar = useDesactivar();
  const publicar = usePublicarEnMuro();

  const [confirmando, setConfirmando] = useState<'cancelar' | 'desactivar' | 'publicar' | null>(null);
  const [verInscritos, setVerInscritos] = useState(false);

  const paso = e ? yaPaso(e.event_date) : false;
  const lleno = e?.max_capacity != null && e.registered_count >= e.max_capacity;

  function cerrar() {
    setVerInscritos(false);
    setConfirmando(null);
    onCerrar();
  }

  const aviso = (err: unknown, porDefecto: string) =>
    Utils.toast(err instanceof Error ? err.message : porDefecto, 'error');

  function hacerInscripcion() {
    if (!e) return;
    inscribirse.mutate(e.id, {
      onSuccess: () => Utils.toast('Inscripción confirmada', 'success'),
      onError: (err) => aviso(err, 'No se pudo completar la inscripción'),
    });
  }

  function confirmar() {
    if (!e) return;
    if (confirmando === 'cancelar') {
      cancelar.mutate(e.id, {
        onSuccess: () => { setConfirmando(null); Utils.toast('Inscripción cancelada', 'info'); },
        onError: (err) => { setConfirmando(null); aviso(err, 'No se pudo cancelar la inscripción'); },
      });
    } else if (confirmando === 'desactivar') {
      desactivar.mutate(e.id, {
        onSuccess: () => { Utils.toast('Evento desactivado', 'info'); cerrar(); },
        onError: (err) => { setConfirmando(null); aviso(err, 'No se pudo desactivar el evento'); },
      });
    } else if (confirmando === 'publicar') {
      publicar.mutate(e.id, {
        onSuccess: (r) => {
          setConfirmando(null);
          Utils.toast(r.ya_existia ? 'Este evento ya estaba publicado en el muro' : 'Evento publicado en el muro',
            r.ya_existia ? 'info' : 'success');
        },
        onError: (err) => { setConfirmando(null); aviso(err, 'No se pudo publicar el evento'); },
      });
    }
  }

  const modalidad = e ? metaDeModalidad(e.modality) : null;

  return (
    <>
      <Drawer
        abierto={id != null}
        onCambio={(v) => { if (!v) cerrar(); }}
        titulo={e?.title ?? 'Evento'}
        descripcion={e ? Utils.fmtDatetime(e.event_date) : undefined}
        ancho="lg"
        pie={e && (
          <>
            <Button variant="ghost" onClick={cerrar}>Cerrar</Button>
            {e.is_registered ? (
              !paso && (
                <Button variant="secondary" onClick={() => setConfirmando('cancelar')}>
                  Cancelar inscripción
                </Button>
              )
            ) : paso ? null : lleno ? (
              <Button disabled>Cupos agotados</Button>
            ) : (
              <Button loading={inscribirse.isPending} onClick={hacerInscripcion}>
                <i className="bi bi-check2-circle" aria-hidden="true" /> Inscribirme
              </Button>
            )}
          </>
        )}
      >
        {consulta.isLoading ? (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-48 w-full rounded-sm" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : consulta.isError ? (
          <EstadoError err={consulta.error} que="el evento" onReintentar={() => void consulta.refetch()} />
        ) : e && modalidad ? (
          <div className="flex flex-col gap-5">
            {e.cover_url && (
              <img src={e.cover_url} alt="" className="h-52 w-full rounded-sm object-cover" />
            )}

            <div className="flex flex-wrap gap-2">
              {e.is_registered && <Badge tone="ok"><i className="bi bi-check-lg" aria-hidden="true" /> Estás inscrito</Badge>}
              {paso && <Badge tone="neutral">Ya pasó</Badge>}
              {!paso && lleno && !e.is_registered && <Badge tone="warn">Cupos agotados</Badge>}
            </div>

            {e.description && (
              <p className="whitespace-pre-line text-3 leading-relaxed text-ink-2 [overflow-wrap:anywhere]">
                {e.description}
              </p>
            )}

            <dl className="grid gap-x-6 gap-y-3 text-3 sm:grid-cols-2">
              <Dato icono="bi-calendar-event" titulo="Fecha">{Utils.fmtDatetime(e.event_date)}</Dato>
              <Dato icono="bi-geo-alt" titulo="Lugar">{e.location || 'Por definir'}</Dato>
              <Dato icono={modalidad.icono} titulo="Modalidad">{modalidad.etiqueta}</Dato>
              <Dato icono="bi-people" titulo="Inscritos">
                {e.max_capacity != null
                  ? `${e.registered_count} de ${e.max_capacity}`
                  : `${e.registered_count} · sin límite de cupos`}
              </Dato>
            </dl>

            <Galeria e={e} />

            {puedeGestionar && (
              <section aria-labelledby="gestion-evento" className="flex flex-col gap-3 border-t border-line pt-4">
                <h3 id="gestion-evento" className="text-3 font-semibold text-brand-ink">Gestión</h3>
                <div className="flex flex-wrap gap-2">
                  <Button variant="secondary" size="sm" onClick={() => onEditar(e)}>
                    <i className="bi bi-pencil" aria-hidden="true" /> Editar
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-expanded={verInscritos}
                    aria-controls="inscritos-del-evento"
                    onClick={() => setVerInscritos((v) => !v)}
                  >
                    <i className="bi bi-people" aria-hidden="true" /> Inscritos ({e.registered_count})
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => setConfirmando('publicar')}>
                    <i className="bi bi-megaphone" aria-hidden="true" /> Publicar en el muro
                  </Button>
                  <Button variant="ghost" size="sm" className="text-danger-ink" onClick={() => setConfirmando('desactivar')}>
                    <i className="bi bi-slash-circle" aria-hidden="true" /> Desactivar
                  </Button>
                </div>
                {verInscritos && <Inscritos idEvento={e.id} />}
              </section>
            )}
          </div>
        ) : null}
      </Drawer>

      <ConfirmDialog
        abierto={confirmando === 'cancelar'}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo="Cancelar tu inscripción"
        descripcion={e ? `Dejarás tu cupo en «${e.title}». Puedes volver a inscribirte mientras queden cupos.` : ''}
        textoConfirmar="Cancelar inscripción"
        destructivo
        cargando={cancelar.isPending}
        onConfirmar={confirmar}
      />
      <ConfirmDialog
        abierto={confirmando === 'desactivar'}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo="Desactivar el evento"
        descripcion="Deja de verse en el portal, pero no se borra: se conservan sus inscritos y sus fotos, y se puede reactivar desde «Mostrar inactivos»."
        textoConfirmar="Desactivar"
        destructivo
        cargando={desactivar.isPending}
        onConfirmar={confirmar}
      />
      <ConfirmDialog
        abierto={confirmando === 'publicar'}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo="Publicar en el muro"
        descripcion="El evento aparecerá en Novedades para todo el portal. Si ya estaba publicado, no se repite."
        textoConfirmar="Publicar"
        cargando={publicar.isPending}
        onConfirmar={confirmar}
      />
    </>
  );
}

function Dato({ icono, titulo, children }: { icono: string; titulo: string; children: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="flex items-center gap-1.5 text-2 font-semibold text-muted">
        <i className={cn('bi', icono)} aria-hidden="true" /> {titulo}
      </dt>
      <dd className="mt-0.5 text-ink [overflow-wrap:anywhere]">{children}</dd>
    </div>
  );
}

/** Las fotos del evento, dentro del detalle. Cada una es un botón que abre el
 *  visor del portal, y lleva a la galería completa, que es donde se gestionan. */
function Galeria({ e }: { e: EventoConGaleria }) {
  const puedeGestionar = Auth.can('eventos', 'manage');
  const fotos = e.gallery ?? [];
  if (!fotos.length && !puedeGestionar) return null;
  const visibles = fotos.slice(0, 6);

  return (
    <section aria-labelledby="galeria-del-evento" className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 id="galeria-del-evento" className="text-3 font-semibold text-brand-ink">
          Galería {fotos.length > 0 && <span className="font-normal text-muted">({fotos.length})</span>}
        </h3>
        <a
          href={`#event-gallery/${e.id}`}
          className="inline-flex min-h-8 items-center gap-1.5 rounded-xs px-2 text-2 font-semibold text-brand-ink
                     hover:bg-sunken focus-visible:focus-anillo pointer-coarse:min-h-11"
        >
          {fotos.length ? 'Ver la galería completa' : 'Subir fotos'}
          <i className="bi bi-arrow-right" aria-hidden="true" />
        </a>
      </div>
      {fotos.length > 0 && (
        <ul className="grid grid-cols-3 gap-2">
          {visibles.map((f, i) => (
            <li key={f.id}>
              <button
                type="button"
                onClick={() => Utils.openLightbox(fotos.map((x) => ({ url: x.url, thumb_url: x.thumb_url })), i)}
                className="block aspect-[4/3] w-full overflow-hidden rounded-sm border border-line focus-visible:focus-anillo"
                aria-label={`Ver la foto ${i + 1} de ${fotos.length}`}
              >
                <img src={f.thumb_url || f.url} alt="" loading="lazy" className="size-full object-cover" />
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Opción 4: quién se inscribió. Se pide al abrirla, no con el detalle: la
 *  lista de correos es de gestión y no hace falta traerla en cada apertura. */
function Inscritos({ idEvento }: { idEvento: number }) {
  const consulta = useInscritos(idEvento, true);
  const lista = consulta.data ?? [];

  return (
    <div id="inscritos-del-evento">
      {consulta.isLoading ? (
        <SkeletonFilas filas={3} columnas={3} />
      ) : consulta.isError ? (
        <EstadoError err={consulta.error} que="los inscritos" onReintentar={() => void consulta.refetch()} />
      ) : !lista.length ? (
        <p className="rounded-sm bg-sunken px-3 py-4 text-center text-2 text-muted">Aún no hay inscritos.</p>
      ) : (
        <div className="overflow-x-auto rounded-sm border border-line">
          <table className="w-full border-collapse text-2">
            <caption className="sr-only">Inscritos en el evento</caption>
            <thead>
              <tr className="border-b border-line bg-sunken text-left text-muted">
                <th scope="col" className="px-3 py-2 font-semibold">Persona</th>
                <th scope="col" className="px-3 py-2 font-semibold">Inscrita</th>
                <th scope="col" className="px-3 py-2 font-semibold">Asistió</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((r) => (
                <tr key={r.user_id} className="border-b border-line last:border-0">
                  <td className="px-3 py-2">
                    <span className="block text-ink [overflow-wrap:anywhere]">{r.full_name}</span>
                    {r.email && <span className="block text-1 text-muted [overflow-wrap:anywhere]">{r.email}</span>}
                  </td>
                  <td className="whitespace-nowrap px-3 py-2 text-ink-2">
                    {r.registered_at ? Utils.fmtDate(r.registered_at) : '—'}
                  </td>
                  <td className="px-3 py-2 text-ink-2">{r.attended ? 'Sí' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
